import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, RouterProvider, createBrowserRouter, useLocation, useParams, useSearchParams } from 'react-router-dom';
import type { TenantPublicConfig } from '@missions/types';
import { AppShell } from './layout/AppShell';
import { SessionProvider, useSession } from './lib/session';
import { tenantLocation } from './lib/tenant';
import { BootScreen } from './lib/wake';
import { Radar } from './components/atoms';
import { MissionControl } from './pages/MissionControl';
import { MyMissions } from './pages/MyMissions';
import { InboxPage } from './pages/Inbox';
import { ProjectPage } from './pages/project/ProjectPage';
import { ProjectsIndex } from './pages/project/ProjectsIndex';
import { NotFound, ComingSoon } from './pages/Misc';
import { SignIn, ForgotPassword, ResetPassword, AcceptInvite, ConfirmEmail } from './pages/auth/AuthPages';
import { RouteError } from './components/ErrorScreen';

const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const BoardsPage = lazy(() => import('./pages/board/BoardsPage'));
const ClaimPage = lazy(() => import('./pages/claim/ClaimPage'));
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage'));

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="boot-load" role="status" aria-live="polite">
      <Radar size={56} />
      <span>{label}</span>
    </div>
  );
}

/** Signed-in area: redirects to /login (with ?next) when there is no session. */
function RequireSession({ children }: { children: ReactNode }) {
  const s = useSession();
  const loc = useLocation();
  if (s.status === 'loading') return <Loading label="Signing you in…" />;
  if (s.status === 'signed-out') {
    const next = loc.pathname + loc.search;
    return <Navigate to={`/login${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}${s.expired ? `${next && next !== '/' ? '&' : '?'}expired=1` : ''}`} replace />;
  }
  return <>{children}</>;
}

/** Links from older emails: /reset-password?token=… → /reset/:token. */
function LegacyResetRedirect() {
  const [sp] = useSearchParams();
  const token = sp.get('token');
  return <Navigate to={token ? `/reset/${encodeURIComponent(token)}` : '/forgot'} replace />;
}

/** Local dev without the Render proxy: /claim/:slug → /offer/:slug. */
function ClaimRedirect() {
  const { slug = '' } = useParams();
  return <Navigate to={`/offer/${slug}`} replace />;
}

const lazyEl = (el: ReactNode) => <Suspense fallback={<Loading />}>{el}</Suspense>;

export function App({ tenant }: { tenant: TenantPublicConfig }) {
  const errorElement = <RouteError />;
  const router = createBrowserRouter(
    [
      { path: '/login', element: <SignIn />, errorElement },
      { path: '/forgot', element: <ForgotPassword />, errorElement },
      { path: '/reset/:token', element: <ResetPassword />, errorElement },
      { path: '/reset-password', element: <LegacyResetRedirect />, errorElement },
      { path: '/invite/:token', element: <AcceptInvite />, errorElement },
      { path: '/confirm-email/:token', element: <ConfirmEmail />, errorElement },
      { path: '/confirm-email', element: <ConfirmEmail />, errorElement },
      // shared links /claim/:slug are proxied to the API's OG share page, which redirects here
      { path: '/offer/:slug', element: <Suspense fallback={<BootScreen />}><ClaimPage /></Suspense>, errorElement },
      { path: '/claim/:slug', element: <ClaimRedirect /> },
      { path: '/onboard', element: lazyEl(<OnboardingPage />), errorElement },
      {
        path: '/',
        element: (
          <RequireSession>
            <AppShell />
          </RequireSession>
        ),
        errorElement,
        children: [
          { index: true, element: <MissionControl /> },
          { path: 'my', element: <MyMissions /> },
          { path: 'my-missions', element: <Navigate to="/my" replace /> },
          { path: 'inbox', element: <InboxPage /> },
          { path: 'projects', element: <ProjectsIndex /> },
          { path: 'p/:projectId', element: <ProjectPage /> },
          { path: 'p/:projectId/m/:missionId', element: <ProjectPage /> },
          { path: 'boards', element: lazyEl(<BoardsPage />) },
          { path: 'boards/:boardId', element: lazyEl(<BoardsPage />) },
          { path: 'settings', element: lazyEl(<SettingsPage />) },
          { path: 'settings/:section', element: lazyEl(<SettingsPage />) },
          { path: 'team', element: <Navigate to="/settings/members" replace /> },
          { path: 'reports', element: <ComingSoon title="Reports" /> },
          { path: 'clients', element: <ComingSoon title="Clients" /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
    { basename: tenantLocation().basename },
  );
  return (
    <SessionProvider tenant={tenant}>
      <RouterProvider router={router} />
    </SessionProvider>
  );
}
